import styles from "./StatusTag.module.css";
import type { TaskStatus, TaskPriority, ProjectStatus, EpicStatus, SprintStatus, SessionUser } from "../../types/interfaces";

type Tone = "neutral" | "brand" | "info" | "warn" | "ok" | "danger";

const TONE: Record<Tone, { fg: string; bg: string }> = {
  neutral: { fg: "var(--q-text-muted)", bg: "var(--q-sunken)" },
  brand: { fg: "var(--q-brand-700)", bg: "var(--q-brand-100)" },
  info: { fg: "var(--q-brand-600)", bg: "var(--q-brand-100)" },
  warn: { fg: "var(--q-warn)", bg: "var(--q-warn-soft)" },
  ok: { fg: "var(--q-ok)", bg: "var(--q-ok-soft)" },
  danger: { fg: "var(--q-danger)", bg: "var(--q-danger-soft)" },
};

export const TASK_STATUS: Record<TaskStatus, { label: string; tone: Tone }> = {
  backlog: { label: "Backlog", tone: "neutral" },
  ready: { label: "Ready", tone: "info" },
  in_progress: { label: "In progress", tone: "brand" },
  in_review: { label: "In review", tone: "warn" },
  done: { label: "Done", tone: "ok" },
};

export const TASK_PRIORITY: Record<TaskPriority, { label: string; tone: Tone }> = {
  low: { label: "Low", tone: "neutral" },
  medium: { label: "Medium", tone: "neutral" },
  high: { label: "High", tone: "warn" },
  urgent: { label: "Urgent", tone: "danger" },
};

export const PROJECT_STATUS: Record<ProjectStatus, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "ok" },
  on_hold: { label: "On hold", tone: "warn" },
  completed: { label: "Completed", tone: "info" },
  archived: { label: "Archived", tone: "neutral" },
};

export const EPIC_STATUS: Record<EpicStatus, { label: string; tone: Tone }> = {
  open: { label: "Open", tone: "neutral" },
  in_progress: { label: "In progress", tone: "brand" },
  done: { label: "Done", tone: "ok" },
};

export const SPRINT_STATUS: Record<SprintStatus, { label: string; tone: Tone }> = {
  planned: { label: "Planned", tone: "neutral" },
  active: { label: "Active", tone: "brand" },
  completed: { label: "Completed", tone: "ok" },
};

export const ROLE: Record<SessionUser["role"], { label: string; tone: Tone }> = {
  admin: { label: "Admin", tone: "brand" },
  user: { label: "Employee", tone: "info" },
  final_user: { label: "Client", tone: "neutral" },
};

interface Props { label: string; tone?: Tone; dot?: boolean; }

export const StatusTag = ({ label, tone = "neutral", dot }: Props) => {
  const t = TONE[tone];
  return (
    <span className={styles.tag} style={{ color: t.fg, background: t.bg }}>
      {dot && <span className={styles.dot} />}
      {label}
    </span>
  );
};

export const TaskStatusTag = ({ status }: { status: TaskStatus }) => (
  <StatusTag {...TASK_STATUS[status]} dot />
);
export const TaskPriorityTag = ({ priority }: { priority: TaskPriority }) => (
  <StatusTag {...TASK_PRIORITY[priority]} />
);
export const ProjectStatusTag = ({ status }: { status: ProjectStatus }) => (
  <StatusTag {...PROJECT_STATUS[status]} dot />
);

export const EpicStatusTag = ({ status }: { status: EpicStatus }) => <StatusTag {...EPIC_STATUS[status]} dot />;
export const SprintStatusTag = ({ status }: { status: SprintStatus }) => <StatusTag {...SPRINT_STATUS[status]} dot />;
export const RoleTag = ({ role }: { role: SessionUser["role"] }) => <StatusTag {...ROLE[role]} />;
